import { clampNonNegativeInt } from './autoRestartView';

/** Restart options stored on loop/restart edge `data` (snake_case, matches definition fields). */
export interface LoopEdgeRestartData {
  readonly auto_restart_enabled?: boolean;
  readonly auto_restart_amount?: number;
  readonly auto_restart_delay_seconds?: number;
  readonly restart_on_return_codes?: readonly number[];
}

const EMPTY_LOOP_EDGE_RESTART: LoopEdgeRestartData = {
  auto_restart_enabled: false,
  auto_restart_amount: 0,
  auto_restart_delay_seconds: 0,
  restart_on_return_codes: [],
};

/** Default max when turning limits on with amount still 0 (off means infinite; enable+0 would stop looping). */
export const DEFAULT_LOOP_EDGE_RESTART_AMOUNT = 1;

/** Parse edge `data` whether it is already an object or a JSON string (parity with BE parseEdgeData). */
export const parseEdgeDataObject = (data: unknown): Record<string, unknown> => {
  let raw: unknown = data;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
};

/**
 * Reads loop-edge restart options from edge `data`, clamping amount/delay to non-negative integers
 * (parity with node-definition auto-restart toSave() and clampNonNegativeInt in autoRestartView).
 */
export const readLoopEdgeRestartData = (data: unknown): LoopEdgeRestartData => {
  const edgeData = parseEdgeDataObject(data);
  const codes = Array.isArray(edgeData.restart_on_return_codes)
    ? edgeData.restart_on_return_codes.map((c) => Number(c)).filter((c) => Number.isInteger(c))
    : [];
  return {
    ...EMPTY_LOOP_EDGE_RESTART,
    auto_restart_enabled: !!edgeData.auto_restart_enabled,
    auto_restart_amount: clampNonNegativeInt(Number(edgeData.auto_restart_amount) || 0),
    auto_restart_delay_seconds: clampNonNegativeInt(Number(edgeData.auto_restart_delay_seconds) || 0),
    restart_on_return_codes: codes,
  };
};

/** Clamp numeric restart fields on a partial patch before merging into edge data. */
export const clampLoopEdgeRestartPatch = (partial: Partial<LoopEdgeRestartData>): Partial<LoopEdgeRestartData> => {
  return {
    ...partial,
    ...(partial.auto_restart_amount !== undefined ? { auto_restart_amount: clampNonNegativeInt(Number(partial.auto_restart_amount) || 0) } : {}),
    ...(partial.auto_restart_delay_seconds !== undefined
      ? { auto_restart_delay_seconds: clampNonNegativeInt(Number(partial.auto_restart_delay_seconds) || 0) }
      : {}),
  };
};

/** Normalize restart-option fields on edge data for the save payload. */
export const normalizeLoopEdgeRestartDataForSave = (data: unknown): Record<string, unknown> | null => {
  if (data == null) {
    return null;
  }
  const normalized = readLoopEdgeRestartData(data);
  return { ...parseEdgeDataObject(data), ...normalized };
};
